import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup } from "@/components/BrandLockup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";

const LOGIN_IMAGE =
  "https://images.unsplash.com/photo-1570133435536-7ececf000ef6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwbW9yb2NjYW4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg3ODkyNTU2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (result.user.force_password_change) {
      navigate("/set-password", { replace: true });
      return;
    }
    navigate(result.user.role === "admin" ? "/admin" : "/dealer", { replace: true });
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2" data-testid="login-page">
      <div className="relative hidden md:block">
        <img
          src={LOGIN_IMAGE}
          alt="Moroccan zellige architecture"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-charcoal/35" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-limestone">
            <BrandLockup connectClassName="text-terracotta" />
          </p>
          <p className="mt-3 max-w-md font-display text-3xl font-light leading-snug text-white">
            The dealer portal for handcrafted Moroccan zellige.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background px-6 py-16">
        <div className="w-full max-w-sm page-enter">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">
            Dealer Access
          </p>
          <h1 className="mt-4 font-display text-4xl font-light tracking-tight text-charcoal">
            Sign in
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-warmgrey">
            Private portal for authorized Zelligio showrooms.
          </p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-6" data-testid="login-form">
            {error && (
              <Alert variant="destructive" data-testid="login-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-warmgrey">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@showroom.com"
                className="h-11 border-border bg-white focus-visible:ring-terracotta"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-warmgrey">
                Password
              </Label>
              <PasswordInput
                id="password"
                data-testid="login-password-input"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 border-border bg-white focus-visible:ring-terracotta"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="h-11 w-full bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-10 text-xs leading-relaxed text-muted-foreground">
            Access is provisioned by Zelligio. Contact your account manager if you
            need credentials or a password reset.
          </p>
        </div>
      </div>
    </div>
  );
}
